import { Component, Input, OnDestroy, OnInit, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NonNullableFormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil, debounceTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { BaseInputSizes } from '../../../../shared/components/input/base-input.component';
import { BaseRadiobuttonItemInterface } from '../../../../shared/interfaces/base-radiobutton-item.interface';
import { BaseButtonSize } from '../../../../shared/components/button/base-button.component';
import { baseEmailValidator } from '../../../../shared/validators/base-email.validator';
import { basePasswordValidator } from '../../../../shared/validators/base-password.validator';
import { UserRegisterCheckResponse, CustomerRegistrationRequest, PspRegistrationRequest } from '../../../../../api';
import { baseAuthActions, BaseCoreState, baseAuthSelectors } from '../../../../_store';
import { BaseApplication } from '../../../../shared/enums/base-application.enum';
import { BaseGenderOptions } from '../../../../shared/constants/base-gender-options';
import { passwordMatchingValidatior } from '../../../../shared/validators/base-confirm-password.validator';
import { baseFormsSelectors } from '../../../../_store/selectors';
import { baseFormsActions } from '../../../../_store/actions';
import { baseIsDeepEqual } from '../../../../shared/helpers/base-is-deep-equal';
import { BaseExternalUrls } from '../../../../shared/enums/base-external-urls.enum';
import { BaseRegisterFormInterface } from '../../_interfaces/base-register-form.interface';
import { PORTAL_SETTINGS } from '../../../../core/token/base-config.token';
import { PortalSettings } from '../../../../core/types/base-portal-settings.type';

@Component({
  selector: 'base-register-form',
  templateUrl: './base-register-form.component.html',
})
export class BaseRegisterFormComponent implements OnInit, OnDestroy {
  @Input() application: BaseApplication;
  applications = BaseApplication;
  inviteToken: string;
  subtitle: string;
  submitted = false;
  inputSize = BaseInputSizes;
  buttonSize = BaseButtonSize;
  titles: BaseRadiobuttonItemInterface[] = BaseGenderOptions;
  destroy$ = new Subject();
  registrationForm: FormGroup<BaseRegisterFormInterface> = this.fb.group(
    {
      name: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [baseEmailValidator()]],
      password: ['', [Validators.required, basePasswordValidator()]],
      confirmPassword: ['', [Validators.required, basePasswordValidator()]],
      gender: [null as BaseRadiobuttonItemInterface | null, [Validators.required]],
      conditionAccepted: [false, [Validators.requiredTrue]],
      privacyPolicyAccepted: [false, [Validators.requiredTrue]],
    },
    { validators: passwordMatchingValidatior }
  );
  baseStazzleLinks = BaseExternalUrls;
  isPsp: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<BaseCoreState>,
    private fb: NonNullableFormBuilder,
    @Inject(PORTAL_SETTINGS) private portalConfig: PortalSettings
  ) {}

  get portalSettings(): PortalSettings {
    return this.portalConfig;
  }

  ngOnInit(): void {
    this.isPsp = this.application === BaseApplication.growth;
    if (this.isPsp) {
      this.registrationForm.addControl(
        'dataProcessingAgreementAccepted',
        new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] })
      );
    }
    this.inviteToken = this.activatedRoute.snapshot.params.token;
    if (this.inviteToken) {
      this.validateToken(this.inviteToken);
    } else {
      this.registrationForm.controls.email.addValidators(Validators.required);
      this.loadRegisterFormFromCache();
    }

    this.registrationForm.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(50))
      .subscribe((registrationFormValue) => {
        const { password, confirmPassword, conditionAccepted, ...form } = registrationFormValue;
        this.store.dispatch(baseFormsActions.setAccountRegisterForm({ payload: form }));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.store.dispatch(baseAuthActions.resetRegistrationCheck());
  }

  register(): void {
    this.submitted = true;

    const formValue = this.registrationForm.getRawValue();

    if (this.registrationForm.invalid) {
      return;
    }

    if (this.inviteToken) {
      this.store.dispatch(
        baseAuthActions.registerUser({
          payload: {
            firstName: formValue.firstName,
            gender: formValue.gender?.value || 1,
            inviteToken: this.inviteToken,
            lastName: formValue.lastName,
            password: formValue.password,
          },
        })
      );
    } else {
      const formData = {
        conditionAccepted: formValue.conditionAccepted,
        email: formValue.email,
        firstName: formValue.firstName,
        gender: formValue.gender?.value || 1,
        lastName: formValue.lastName,
        name: formValue.name,
        password: formValue.password,
      };

      switch (this.application) {
        case BaseApplication.medical:
          this.store.dispatch(
            baseAuthActions.registerCustomer({
              payload: formData as CustomerRegistrationRequest,
            })
          );
          break;
        case BaseApplication.growth:
          this.store.dispatch(
            baseAuthActions.registerPSP({
              payload: formData as PspRegistrationRequest,
            })
          );
          break;
        case BaseApplication.business:
          this.store.dispatch(
            baseAuthActions.registerBusinessCustomer({
              payload: formData as CustomerRegistrationRequest,
            })
          );
          break;
      }
    }
  }

  goTo(link: string | undefined): void {
    if (!link) {
      return;
    }

    window.open(link, '_blank');
  }

  clearRegisterFormCache(): void {
    this.store.dispatch(baseFormsActions.clearAccountRegisterForm());

    const name = this.registrationForm.getRawValue().name;

    if (this.inviteToken) {
      this.registrationForm.reset({
        name,
      });
    } else {
      this.registrationForm.reset();
    }
  }

  private validateToken(token: string): void {
    this.store.dispatch(baseAuthActions.loadRegistrationCheck({ payload: token }));

    this.store
      .select(baseAuthSelectors.getRegistrationCheckData)
      .pipe(
        filter((state) => !!state?.data),
        map((state) => state as UserRegisterCheckResponse),
        take(1)
      )
      .subscribe(({ data }) => {
        this.registrationForm.controls.name.setValue(data.organization.legalName);
        this.registrationForm.controls.name.disable();
        this.registrationForm.controls.gender.setValue(
          this.titles.find((title) => title.value === data.gender) || { value: '', label: '' }
        );
        this.registrationForm.controls.firstName.setValue(data.firstName);
        this.registrationForm.controls.lastName.setValue(data.lastName);
      });
  }

  private loadRegisterFormFromCache() {
    this.store
      .select(baseFormsSelectors.getAccountRegisterForm)
      .pipe(
        take(1),
        filter((state) => !!state?.data),
        map((state) => state.data)
      )
      .subscribe((registerFormValue) => {
        if (!baseIsDeepEqual(registerFormValue, this.registrationForm.value)) {
          this.registrationForm.patchValue(
            { ...registerFormValue, password: '', confirmPassword: '', conditionAccepted: false },
            { emitEvent: false }
          );
        }
      });
  }
}
